import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { User, Building2, MapPin, Shield, Activity } from 'lucide-react';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
export function AppProfile() {
  const {
    user,
    profile,
    preferences,
    business
  } = useAuth();
  return <AppLayout data-id="element-358">
      <div className="max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-359">
        <div className="mb-8" data-id="element-360">
          <h1 className="text-3xl font-bold text-primary mb-2" data-id="element-361">Profile</h1>
          <p className="text-secondary" data-id="element-362">Your account and onboarding details.</p>
        </div>

        <div className="space-y-6" data-id="element-363">
          {/* Account Info */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm" data-id="element-364">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-borderColor" data-id="element-365">
              <User className="w-6 h-6 text-violetBlue" data-id="element-366" />
              <h2 className="text-xl font-bold text-primary" data-id="element-367">Account</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-368">
              <div data-id="element-369">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-370">
                  Name
                </p>
                <p className="font-medium text-primary" data-id="element-371">{user?.name || '-'}</p>
              </div>
              <div data-id="element-372">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-373">
                  Email
                </p>
                <p className="font-medium text-primary" data-id="element-374">{user?.email || '-'}</p>
              </div>
            </div>
          </motion.div>

          {/* Personal Profile */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm" data-id="element-375">
            <div className="flex items-center gap-3 mb-4 pb-4 border-b border-borderColor" data-id="element-376">
              <Shield className="w-6 h-6 text-turquoise" data-id="element-377" />
              <h2 className="text-xl font-bold text-primary" data-id="element-378">
                Personal Profile
              </h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-379">
              <div data-id="element-380">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-381">
                  Province
                </p>
                <p className="font-medium text-primary" data-id="element-382">
                  {profile?.province || '-'}
                </p>
              </div>
              <div data-id="element-383">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-384">
                  Completing For
                </p>
                <p className="font-medium text-primary capitalize" data-id="element-385">
                  {profile?.completingFor || '-'}
                </p>
              </div>
              <div data-id="element-386">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-387">
                  Children / Dependants
                </p>
                <p className="font-medium text-primary" data-id="element-388">
                  {profile?.children || '-'}
                </p>
              </div>
              <div data-id="element-389">
                <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-390">
                  Property Owner
                </p>
                <p className="font-medium text-primary" data-id="element-391">
                  {profile?.property || '-'}
                </p>
              </div>
            </div>
          </motion.div>

          {/* Assessment Preferences */}
          {preferences && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.15
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm" data-id="element-392">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-borderColor" data-id="element-393">
                <Activity className="w-6 h-6 text-violetBlue" data-id="element-394" />
                <h2 className="text-xl font-bold text-primary" data-id="element-395">
                  Assessment Preferences
                </h2>
              </div>
              <div className="space-y-4" data-id="element-396">
                <div data-id="element-397">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-398">
                    Starting With
                  </p>
                  <p className="font-medium text-primary capitalize" data-id="element-399">
                    {preferences.startWith || '-'}
                  </p>
                </div>
                {preferences.motivations && preferences.motivations.length > 0 && <div data-id="element-400">
                      <p className="text-xs text-secondary uppercase tracking-wider mb-2" data-id="element-401">
                        Areas of Interest
                      </p>
                      <div className="flex flex-wrap gap-2" data-id="element-402">
                        {preferences.motivations.map(m => <span key={m} className="px-3 py-1 bg-aliceBlue text-primary text-xs font-medium rounded-full border border-borderColor" data-id="element-403">
                            {m}
                          </span>)}
                      </div>
                    </div>}
              </div>
            </motion.div>}

          {/* Business Profile */}
          {business?.hasBusiness && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-xl border border-borderColor p-6 shadow-sm" data-id="element-404">
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-borderColor" data-id="element-405">
                <Building2 className="w-6 h-6 text-violetBlue" data-id="element-406" />
                <h2 className="text-xl font-bold text-primary" data-id="element-407">
                  Business Profile
                </h2>
              </div>
              <div className="grid sm:grid-cols-2 gap-4" data-id="element-408">
                <div data-id="element-409">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-410">
                    Business Name
                  </p>
                  <p className="font-medium text-primary" data-id="element-411">
                    {business.name || '-'}
                  </p>
                </div>
                <div data-id="element-412">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-413">
                    Type
                  </p>
                  <p className="font-medium text-primary" data-id="element-414">
                    {business.type || '-'}
                  </p>
                </div>
                <div data-id="element-415">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-416">
                    Province
                  </p>
                  <p className="font-medium text-primary" data-id="element-417">
                    {business.province || '-'}
                  </p>
                </div>
                <div data-id="element-418">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-419">
                    Employees
                  </p>
                  <p className="font-medium text-primary" data-id="element-420">
                    {business.employees || '-'}
                  </p>
                </div>
                <div data-id="element-421">
                  <p className="text-xs text-secondary uppercase tracking-wider mb-1" data-id="element-422">
                    Industry
                  </p>
                  <p className="font-medium text-primary" data-id="element-423">
                    {business.industry || '-'}
                  </p>
                </div>
              </div>
            </motion.div>}
        </div>
      </div>
    </AppLayout>;
}